import { ChangeDetectionStrategy, Component, computed, effect, inject, Input, OnInit, signal, Signal, untracked, HostListener } from '@angular/core';
import { PlayerService } from '../../data-access/player.service';
import { RoundedIconButton } from 'app/shared/components/rounded-icon-button/rounded-icon-button';
import { SkinningService } from '@core/skin';
import { clpp } from '@castlabs/prestoplay';
import { PlayerControlsSeekComponent } from './components/player-controls-seek-preview/player-controls-seek-preview';
import { CommonModule, DatePipe } from '@angular/common';
import { SliderModule } from 'primeng/slider';
import { FormsModule } from '@angular/forms';
import { PlayerControlsSeriesEpisodesComponent } from './components/player-controls-series-episodes/player-controls-series-episodes';
import { PlayerControlsSettingsComponent } from './components/player-controls-settings/player-controls-settings';
import { IThumbnailConfig } from './components/player-controls-seek-preview/player-controls-seek-preview-helper';
import { tap } from 'rxjs';
import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
import { toSignal } from '@angular/core/rxjs-interop';
import { LiveTimelineService } from '../../data-access/live-timeline.service';
import { TranslocoPipe } from '@jsverse/transloco';

@Component({
  selector: 'player-controls',
  imports: [
    CommonModule,
    RoundedIconButton,
    PlayerControlsSeekComponent,
    PlayerControlsSeriesEpisodesComponent,
    PlayerControlsSettingsComponent,
    DatePipe,
    FormsModule,
    SliderModule,
    TranslocoPipe
  ],
  templateUrl: './player-controls.component.html',
  styleUrl: './player-controls.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class PlayerControlsComponent {
  public skinning = inject(SkinningService);
  private playerService = inject(PlayerService);
  private breakpointObserver = inject(BreakpointObserver);
  private liveTimeline = inject(LiveTimelineService);

  public readonly item: Signal<any> = this.playerService.getItem();
  public isMuted = this.playerService.isPlayerMuted();
  public isFullscreen = this.playerService.isFullscreen();
  private state = this.playerService.getState();
  // Stacked (compact) controls for phones in either orientation and tablets held portrait -
  // the single-row desktop layout does not fit there. Tablet landscape and up keep desktop.
  public isCompactLayout = toSignal(this.breakpointObserver.observe([Breakpoints.Handset, Breakpoints.TabletPortrait]));
  public isCurrentItemLive = this.playerService.isCurrentItemLive;
  public volume = this.playerService.getVolume();
  private currentEpg = this.liveTimeline.currentEpg;

  public progress = computed(() => {
    const progress = this.playerService.getProgress();
    return this.hasLength() ? progress() : 0;
  });
  public duration = computed(() => {
    // Read progress so this recomputes on each timeupdate tick, but gate the returned value
    // on the duration itself being a real finite length — NOT on progress() being truthy.
    // At position 0 (e.g. right after "start over") progress() is 0, which would otherwise
    // collapse the duration to 00:00:00 instead of showing the stream length.
    this.progress();
    return this.hasLength() ? this.playerService.getDuration() : 0;
  })
  /**
   * A real, finite stream length. The engine answers **-1** while it holds no media at all, and
   * Infinity on a live manifest — neither is a length, and -1 in particular is not falsy, so it
   * travelled straight into the timeline below.
   */
  private hasLength = computed(() => {
    this.playerService.getProgress()();
    const duration = this.playerService.getDuration();
    return Number.isFinite(duration) && duration > 0;
  })
  /**
   * Scale for the timeline slider. p-slider paints `value > max` as a FULL bar, so the engine's
   * -1 made the first playback after a page load look finished for as long as its stream took to
   * start (DRM + manifest — seconds on a historic EPG event). A later playback hid it: the engine
   * still reported the PREVIOUS stream's length, which put the position near 0% instead. With no
   * length yet, a one-second scale at position 0 reads as the empty timeline it is.
   */
  public seekMax = computed(() => this.duration() || 1);
  public itemTitle = computed(() => {
    if (this.isCurrentItemLive()) {
      return this.currentEpg()?.title || this.item()?.title;
    }
    return this.item()?.title;
  });
  public isPlaying = computed(() => {
    return this.state() == clpp.Player.State.PLAYING
  })
  private seriesEpisodeWatchData = this.playerService.getSeriesEpisodeWatchData();
  public readonly isSeries = computed<boolean>(() => {
    return Boolean(this.seriesEpisodeWatchData())
  })

  public isLive = this.liveTimeline.isLive;

  // Live TV cannot play ahead of the live edge, so speeds above 1x are greyed out there;
  // once the customer is behind live (rewound, paused or slowed down) they are watching
  // DVR content and every rate applies. Catching up with the edge flips this back, and
  // the settings menu resets a faster rate to 1 when it does. Without a DVR window there
  // is no "behind live" to speed through — the playhead may still trail the manifest edge
  // by more than isLive's tolerance (the player's live delay), so don't rely on it there.
  public maxSpeed = computed(() => {
    return this.isCurrentItemLive() && (this.isLive() || !this.isSeekEnabled()) ? 1 : Infinity;
  })

  // Slowing live TV down drifts behind the edge, which needs the channel's DVR window —
  // and is a way of pausing live, so it follows the same permission.
  public minSpeed = computed(() => {
    return this.isCurrentItemLive() && (!this.isSeekEnabled() || !this.isPauseEnabled()) ? 1 : 0;
  })

  // Live pause / scrub / start-over permissions — shared with the compact (cast) controls.
  public isSeekEnabled = this.liveTimeline.isSeekEnabled;
  public isRestartEnabled = this.liveTimeline.isRestartEnabled;
  public isPauseEnabled = this.liveTimeline.isPauseEnabled;

  // Live timeline — shared with the compact/floating controls so the two cannot drift.
  public isHistoricEpgEnabled = this.liveTimeline.isHistoricEpgEnabled;
  public historicEpgFloor = this.liveTimeline.historicEpgFloor;
  public liveTimeEnd = this.liveTimeline.liveTimeEnd;
  public liveTimeStart = this.liveTimeline.liveTimeStart;
  public liveSeekMin = this.liveTimeline.liveSeekMin;
  public liveProgress = this.liveTimeline.liveProgress;

  public seekPosition = signal(0);
  public cursorPosition = signal(0);
  public thumbnailConfig = signal<IThumbnailConfig | null>(null);
  private lastState: clpp.Player.State | null = null;

  constructor() {
    effect(() => {
      const state = this.state()

      if (state == clpp.Player.State.BUFFERING && this.lastState == clpp.Player.State.PREPARING) {
        untracked(() => {
          const item = this.item();
          const seriesEpisodeWatchData = this.seriesEpisodeWatchData();
          const bookmark = seriesEpisodeWatchData ? seriesEpisodeWatchData?.watchData?.bookmarkEpisodes.find(episode => episode.episodeReferenceId == item.referenceId)?.bookmark : item.bookmark;
          if (bookmark) {
            this.playerService.setPosition(bookmark).subscribe();
          }
        })
      }

      this.lastState = state;
    })

    effect(() => {
      const item = this.item()
      if (item?.thumbnailsDefinitionUrl) {
        this.playerService.getThumbnailConfig(item.thumbnailsDefinitionUrl).pipe(
          tap(data => this.thumbnailConfig.set(data))
        ).subscribe();
      } else {
        this.thumbnailConfig.set(null);
      }
    })
  }

  onPlay() {
    // Resume at live where the channel can't be scrubbed — and where the programme now airing
    // forbids pause (the pause began in an earlier one).
    if (!this.isSeekEnabled() || (this.isCurrentItemLive() && !this.isPauseEnabled())) {
      this.onSeekLive(Date.now());
    }

    this.playerService.play().subscribe();
  }

  onPause() {
    // The keyboard (space / k) reaches here past the disabled button.
    if (!this.isPauseEnabled()) return;
    this.playerService.pause().subscribe();
  }

  onReplay() {
    if (!this.isSeekEnabled() || !this.isRestartEnabled()) return;
    // Live: position 0 doesn't exist in the sliding DVR window — the engine would clamp
    // the seek to seekRange.start, which advances in real time. Seek to the programme
    // start via the epoch-based live mapping instead (clamps to the DVR window and the
    // historicEpgHours floor, same as the scrubber).
    if (this.isCurrentItemLive()) {
      const start = this.liveTimeStart();
      if (start) {
        this.onSeekLive(Number(start));
        return;
      }
    }
    // "Start over": seek to the beginning, then resume playback. Without the play() the player
    // stays paused/ended at position 0 and the user would have to press play a second time.
    this.playerService.setPosition(0).subscribe(() => this.playerService.play().subscribe());
  }

  onSeek(value: number) {
    this.playerService.setPosition(value).subscribe();
  }

  onLive() {
    this.onSeekLive(Date.now());
  }

  onSeekLive(value: number) {
    this.liveTimeline.seekToTimestamp(value).subscribe();
  }

  setSeek(value: number) {
    // The keyboard (arrows / j / l) reaches here past the hidden skip buttons.
    if (!this.isSeekEnabled()) return;
    this.playerService.setPosition(this.playerService.getProgress()() + value).subscribe();
  }

  togglePip() {
    this.playerService.togglePip();
  }

  toggleFullscreen() {
    this.playerService.toggleFullscreen();
  }

  toggleMute() {
    this.playerService.toggleMute();
  }

  onVolume(percentage: number) {
    this.playerService.setVolume(percentage);
  }

  setSpeed(element: EventTarget | null) {
    const speed = (element as any).value;
    this.playerService.setSpeed(speed);
  }

  onSeekHover(event: MouseEvent) {
    const slider = event.currentTarget as HTMLElement;
    const rect = slider.getBoundingClientRect();

    // Calculate mouse position as a fraction (0-1)
    const percentage = Math.min(Math.max((event.clientX - rect.left) / rect.width, 0), 1);

    // Convert fraction to seconds
    const positionInSeconds = Math.round(percentage * this.duration());

    this.seekPosition.set(positionInSeconds);
    this.cursorPosition.set(event.clientX);
  }

  onSeekClearHover() {
    this.seekPosition.set(null);
  }

  @HostListener('window:keydown', ['$event'])
  handleKeyboardEvent(event: KeyboardEvent) {
    if (event.target instanceof HTMLInputElement || event.target instanceof HTMLTextAreaElement) {
      return;
    }

    switch (event.key.toLowerCase()) {
      case ' ':
      case 'k':
        // preventDefault even on auto-repeat, or the held key scrolls the page — but toggle only
        // on the initial press: each toggle reports a play/pause event to the backend, and a held
        // key repeats keydown ~30x/s.
        event.preventDefault();
        if (!event.repeat) {
          this.isPlaying() ? this.onPause() : this.onPlay();
        }
        break;
      case 'arrowleft':
      case 'j':
        this.setSeek(-5);
        break;
      case 'arrowright':
      case 'l':
        this.setSeek(5);
        break;
      case 'arrowup':
        event.preventDefault();
        this.onVolume(Math.min(100, this.volume() + 5));
        break;
      case 'arrowdown':
        event.preventDefault();
        this.onVolume(Math.max(0, this.volume() - 5));
        break;
      case 'm':
        this.toggleMute();
        break;
      case 'f':
        this.toggleFullscreen();
        break;
    }
  }
}
